import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Home, Trophy, ShieldCheck, LayoutDashboard, Settings, Crown, ArrowDownToLine, ArrowUpFromLine,
  ChevronDown, PanelLeftClose, PanelLeftOpen, Sparkles,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { GAMES } from "@/lib/games";
import { useRace } from "@/lib/useRace";
import { formatAmount } from "@/lib/api";
import { Logo } from "./Logo";

const testId = (label) => `nav-${label.toLowerCase().replace(/\s|\./g, "-")}`;

function Item({ to, label, icon: Icon, collapsed, end, accent, tid }) {
  return (
    <NavLink to={to} end={end} title={collapsed ? label : undefined} data-testid={tid || testId(label)}
      className={({ isActive }) => `side-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`}>
      <Icon size={18} style={accent ? { color: accent } : undefined} className="shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

function Group({ title, collapsed, children }) {
  return (
    <div className="mt-4">
      {!collapsed && <div className="px-4 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{title}</div>}
      {collapsed && <div className="hairline mx-4 my-2" />}
      <div className="flex flex-col gap-0.5 px-2">{children}</div>
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const race = useRace();
  const [gamesOpen, setGamesOpen] = useState(true);
  const showGames = collapsed || gamesOpen;

  return (
    <aside className={`hidden md:flex fixed left-0 top-0 h-screen z-40 flex-col sidebar ${collapsed ? "w-[76px]" : "w-[240px]"}`} data-testid="sidebar">
      <div className={`flex items-center h-16 px-3 border-b border-white/[0.06] ${collapsed ? "justify-center" : "justify-between"}`}>
        <NavLink to="/" className="flex items-center gap-3 min-w-0" data-testid="sidebar-logo">
          <Logo size="w-10 h-10 text-sm" />
          {!collapsed && <div className="font-display font-black tracking-tighter leading-none"><span className="gold-text">FMI</span><div className="text-[9px] text-zinc-500 tracking-[0.25em] mt-0.5">CASINO</div></div>}
        </NavLink>
        {!collapsed && (
          <button onClick={onToggle} className="btn-ghost rounded-lg p-1.5 text-zinc-500" data-testid="sidebar-toggle" title="Collapse"><PanelLeftClose size={16} /></button>
        )}
      </div>
      {collapsed && (
        <button onClick={onToggle} className="mx-auto mt-3 btn-ghost rounded-lg p-1.5 text-zinc-500" data-testid="sidebar-toggle" title="Expand"><PanelLeftOpen size={16} /></button>
      )}

      <nav className="flex-1 overflow-y-auto pb-6 sidebar-scroll">
        <Group title="Casino" collapsed={collapsed}>
          <Item to="/" end label="Lobby" icon={Home} collapsed={collapsed} />
          {!collapsed && (
            <button onClick={() => setGamesOpen((o) => !o)} className="side-item justify-between" data-testid="nav-originals-toggle">
              <span className="flex items-center gap-3"><Sparkles size={18} className="text-amber-400" /> FMI Originals</span>
              <ChevronDown size={14} className={`text-zinc-500 ${gamesOpen ? "rotate-180" : ""}`} style={{ transition: "transform 200ms ease" }} />
            </button>
          )}
          {showGames && GAMES.map((g) => (
            <Item key={g.slug} to={`/games/${g.slug}`} label={g.name} icon={g.icon} accent={g.accent} collapsed={collapsed}
              tid={g.slug === "megaslots" ? "nav-mega" : testId(g.name)} />
          ))}
        </Group>

        <Group title="Rewards" collapsed={collapsed}>
          <Item to="/leaderboard" label="Weekly Race" icon={Trophy} collapsed={collapsed} tid="nav-leaders" />
          {user && <Item to="/vip" label="VIP Club" icon={Crown} collapsed={collapsed} tid="nav-vip" />}
        </Group>

        {user && (
          <Group title="Wallet" collapsed={collapsed}>
            <Item to="/deposit" label="Deposit" icon={ArrowDownToLine} collapsed={collapsed} />
            <Item to="/withdraw" label="Withdraw" icon={ArrowUpFromLine} collapsed={collapsed} />
            <Item to="/dashboard" label="Dashboard" icon={LayoutDashboard} collapsed={collapsed} />
          </Group>
        )}

        <Group title="Trust" collapsed={collapsed}>
          <Item to="/provably-fair" label="Provably Fair" icon={ShieldCheck} collapsed={collapsed} tid="nav-prov-fair" />
          {user?.role === "admin" && <Item to="/admin" label="Admin" icon={Settings} collapsed={collapsed} />}
        </Group>
      </nav>

      {!collapsed && (
        <div className="m-3 p-3 rounded-xl border border-amber-500/20 bg-amber-500/5" data-testid="sidebar-promo">
          <div className="text-[10px] uppercase tracking-widest text-amber-400">Weekly race</div>
          <div className="text-xs text-zinc-400 mt-1"><span className="font-mono-num text-white" data-testid="sidebar-race-pool">{formatAmount(race?.pool ?? 0, "FMI")}</span> FMI pool. Top 10 wagerers paid every Monday.</div>
        </div>
      )}
    </aside>
  );
}
